import { useState, useEffect, useCallback, useRef } from "react";
import { companyApi } from "@/services/api";

interface TradeStatus {
  tradeId: string;
  status: string;
  cainvestStatus?: string;
  reconciliationStatus?: string;
  settledAt?: string;
  completedAt?: string;
  errorMessage?: string;
  createdAt: string;
}

interface UseTradeStatusOptions {
  // Polling interval in ms (default: 5000ms)
  pollingInterval?: number;
  // Auto stop polling when status is final (COMPLETED, FAILED, EXPIRED)
  autoStopOnFinal?: boolean;
  // Callback when status changes
  onStatusChange?: (status: TradeStatus) => void;
  // Callback when status becomes COMPLETED
  onCompleted?: (status: TradeStatus) => void;
  // Callback when status becomes FAILED
  onFailed?: (status: TradeStatus) => void;
}

interface UseTradeStatusReturn {
  status: TradeStatus | null;
  loading: boolean;
  error: string | null;
  isPolling: boolean;
  startPolling: () => void;
  stopPolling: () => void;
  refresh: () => Promise<void>;
}

const FINAL_STATUSES = ["COMPLETED", "FAILED", "EXPIRED"];

export function useTradeStatus(
  conversionId: string | null,
  options: UseTradeStatusOptions = {}
): UseTradeStatusReturn {
  const {
    pollingInterval = 5000,
    autoStopOnFinal = true,
    onStatusChange,
    onCompleted,
    onFailed,
  } = options;

  const [status, setStatus] = useState<TradeStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  const previousStatusRef = useRef<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!conversionId) return;

    try {
      setLoading(true);
      const data = await companyApi.conversion.getTradeStatus(conversionId);
      setStatus(data);
      setError(null);

      // Check for status change
      if (previousStatusRef.current !== data.status) {
        onStatusChange?.(data);

        // Callbacks for specific status
        if (data.status === "COMPLETED") {
          onCompleted?.(data);
        } else if (data.status === "FAILED") {
          onFailed?.(data);
        }

        previousStatusRef.current = data.status;
      }

      // Stop polling if final status
      if (autoStopOnFinal && FINAL_STATUSES.includes(data.status)) {
        stopPolling();
      }

      return data;
    } catch (err: any) {
      setError(err.message || "Erro ao buscar status");
      return null;
    } finally {
      setLoading(false);
    }
  }, [conversionId, autoStopOnFinal, onStatusChange, onCompleted, onFailed]);

  const startPolling = useCallback(() => {
    if (!conversionId || intervalRef.current) return;

    setIsPolling(true);
    // Fetch immediately
    fetchStatus();
    // Then set interval
    intervalRef.current = setInterval(fetchStatus, pollingInterval);
  }, [conversionId, pollingInterval, fetchStatus]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPolling(false);
  }, []);

  const refresh = useCallback(async () => {
    await fetchStatus();
  }, [fetchStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Reset when conversionId changes
  useEffect(() => {
    setStatus(null);
    setError(null);
    previousStatusRef.current = null;
    stopPolling();
  }, [conversionId, stopPolling]);

  return {
    status,
    loading,
    error,
    isPolling,
    startPolling,
    stopPolling,
    refresh,
  };
}
